import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { all_routes } from "../../router/all_routes";
import Api from "../../../core/data/redux/utils/apiCall";
import { EAttestationDetailsBody } from "../eAttestation/EAttestationDetails";
import {
  approveRejectEAttestation,
  findOneEAttestationRequest,
  getApiMessage,
  isEaActionable,
  pick,
} from "../eAttestation/eAttestation.service";

const EAttestationApproval: React.FC = () => {
  const { requestID } = useParams<{ requestID: string }>();
  const navigate = useNavigate();
  const routes = all_routes;
  const [detail, setDetail] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [remarks, setRemarks] = useState("");
  const [pendingAction, setPendingAction] = useState<"A" | "R" | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const refId = requestID ? decodeURIComponent(requestID) : "";

  useEffect(() => {
    const load = async () => {
      if (!refId) {
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const data = await findOneEAttestationRequest(refId);
        setDetail(data);
      } catch (err: any) {
        setDetail(null);
        toast.error(Api.getErrorMessage(err) || "Failed to load request details");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [refId]);

  const recordId = pick(detail, "Id", "id");
  const actionable = isEaActionable(detail);

  const requireRemarks = () => {
    if (!remarks.trim()) {
      toast.error("Remarks is required");
      return false;
    }
    return true;
  };

  const submitAction = async (action: "A" | "R") => {
    if (!recordId) {
      toast.error("Request id not found");
      return;
    }
    if (!requireRemarks()) return;
    setSubmitting(true);
    try {
      const res: any = await approveRejectEAttestation({
        id: recordId,
        action,
        remarks: remarks.trim(),
      });
      toast.success(
        getApiMessage(
          res,
          action === "A" ? "Request approved successfully" : "Request rejected successfully"
        )
      );
      navigate(routes.todoWorklist.tasks);
    } catch (err: any) {
      toast.error(Api.getErrorMessage(err) || "Action failed");
    } finally {
      setSubmitting(false);
      setPendingAction(null);
    }
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div className="my-auto mb-2">
            <h3 className="page-title mb-1">E-Attestation Approval</h3>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={routes.adminDashboard}>Dashboard</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to={routes.todoWorklist.tasks}>To-Do Work List</Link>
                </li>
                <li className="breadcrumb-item active">E-Attestation Approval</li>
              </ol>
            </nav>
          </div>
          <Link to={routes.todoWorklist.tasks} className="btn btn-light">
            Back
          </Link>
        </div>

        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-danger" role="status" />
          </div>
        ) : !detail ? (
          <div className="card border-0 shadow-sm">
            <div className="card-body text-center py-5 text-muted">No records found</div>
          </div>
        ) : (
          <>
            <EAttestationDetailsBody detail={detail} />

            {actionable ? (
              <>
                <div className="card border-0 shadow-sm mb-3">
                  <div className="card-header bg-white">
                    <h5 className="mb-0 fw-bold">Approver Remarks</h5>
                  </div>
                  <div className="card-body">
                    <label className="form-label fw-semibold">
                      Remarks <span className="text-danger">*</span>
                    </label>
                    <textarea
                      className="form-control"
                      rows={4}
                      value={remarks}
                      onChange={(e) => setRemarks(e.target.value)}
                      placeholder="Enter remarks"
                      required
                    />
                  </div>
                </div>
                <div className="d-flex justify-content-end gap-2 mb-4">
                  <button
                    type="button"
                    className="btn btn-danger"
                    disabled={submitting}
                    onClick={() => {
                      if (!requireRemarks()) return;
                      setPendingAction("R");
                    }}
                  >
                    Reject
                  </button>
                  <button
                    type="button"
                    className="btn btn-success"
                    disabled={submitting}
                    onClick={() => {
                      if (!requireRemarks()) return;
                      setPendingAction("A");
                    }}
                  >
                    {submitting ? <span className="spinner-border spinner-border-sm me-1" /> : null}
                    Approve
                  </button>
                </div>
              </>
            ) : null}
          </>
        )}

        {pendingAction ? (
          <div className="modal fade show d-block" tabIndex={-1} style={{ background: "rgba(0,0,0,0.45)" }}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    {pendingAction === "A" ? "Approve Request" : "Reject Request"}
                  </h5>
                  <button type="button" className="btn-close" onClick={() => setPendingAction(null)} />
                </div>
                <div className="modal-body">
                  Are you sure you want to {pendingAction === "A" ? "approve" : "reject"} this request?
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-light" disabled={submitting} onClick={() => setPendingAction(null)}>
                    Cancel
                  </button>
                  <button
                    type="button"
                    className={`btn ${pendingAction === "A" ? "btn-success" : "btn-danger"}`}
                    disabled={submitting}
                    onClick={() => submitAction(pendingAction)}
                  >
                    {submitting ? <span className="spinner-border spinner-border-sm me-1" /> : null}
                    {pendingAction === "A" ? "Approve" : "Reject"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default EAttestationApproval;
